import { useMemo } from 'react';

import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import { AssetGovernanceFields, OwnershipNature, ShareType } from 'src/types/governance.types';

export interface DistributedShares {
  distributedShares: number;
  totalShares: number;
  sharesPercentage: number;
  shareType?: ShareType;
}

export const useDistributedShares = (): DistributedShares => {
  const { data: asset } = useFetchAssetDetails();
  const { data: membersData } = useFetchMembers();

  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const shareType = governance?.shareType;
  const isPercentageMode = shareType === ShareType.PERCENTAGE;
  const totalShares = isPercentageMode ? 100 : (governance?.totalShares ?? 100);

  const distributedShares = useMemo(() => {
    if (!membersData?.members) return 0;
    const ownerId = membersData.owner?.id;
    const ownerShares = governance?.ownerShares;
    const ownerOwnershipNature = governance?.ownerOwnershipNature;
    return membersData.members.reduce((sum, m) => {
      const memberData = m as unknown as Record<string, unknown>;
      const isAssetOwner = !!ownerId && m.user.id === ownerId;
      // The owner's governance lives on the asset (ownerShares /
      // ownerOwnershipNature), not on the AssetMember row backfilled by
      // migration 20260608121500. Fall back to the asset values when the
      // member row is empty for the owner.
      const effectiveNature =
        isAssetOwner && memberData.ownershipNature == null
          ? ownerOwnershipNature
          : memberData.ownershipNature;
      if (effectiveNature === OwnershipNature.USUFRUIT) return sum;
      const memberShares = memberData.shares;
      const effectiveShares =
        isAssetOwner && (memberShares == null || memberShares === 0)
          ? (ownerShares ?? 0)
          : typeof memberShares === 'number'
            ? memberShares
            : 0;
      return sum + effectiveShares;
    }, 0);
  }, [
    membersData?.members,
    membersData?.owner?.id,
    governance?.ownerShares,
    governance?.ownerOwnershipNature,
  ]);

  const sharesPercentage =
    totalShares > 0 ? Math.min((distributedShares / totalShares) * 100, 100) : 0;

  return { distributedShares, totalShares, sharesPercentage, shareType };
};
